/**
 * Live stream view for orders: real-time feed with filters.
 * Native mobile app style with modern lifecycle slider.
 */

import {
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
  useMemo,
  useEffect,
} from "react";
import { IconTicket } from "../../../components/Icons.tsx";
import { Loader } from "../../../components/Loader.tsx";
import { safeShowAlert } from "../../../lib/telegram.ts";
import { useDebouncedValue } from "../../../lib/use-debounce.ts";
import { OrderCard } from "./components/OrderCard.tsx";
import { OrderFilters } from "./components/OrderFilters.tsx";
import {
  OrderStatusSlider,
  type OrderLifecycleStatus,
  getBackendStatusFilter,
  matchesVerifiedFilter,
  isValidLifecycleStatus,
} from "./components/OrderStatusSlider.tsx";
import { useAdminOrders } from "../shared/hooks/useAdminOrders";
import {
  useGenerateTickets,
  useResendTickets,
} from "../tickets/hooks/useTicketMutations";

/** Session storage keys for state restoration from ticket view */
const SCROLL_STORAGE_KEY = "admin_ticket_view_scroll";
const EXPANDED_ORDER_STORAGE_KEY = "admin_ticket_view_expanded_order";

const LIFECYCLE_FILTER_STORAGE_KEY = "admin_orders_lifecycle_filter";

/** Polling interval: 10 seconds */
const POLLING_INTERVAL_MS = 10_000;

export function OrdersLiveStream() {
  const [lifecycleStatus, setLifecycleStatus] = useState<OrderLifecycleStatus>(
    () => {
      const saved = sessionStorage.getItem(LIFECYCLE_FILTER_STORAGE_KEY);
      return isValidLifecycleStatus(saved) ? saved : "all";
    }
  );
  const [searchInput, setSearchInput] = useState<string>("");
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [processingOrderId, setProcessingOrderId] = useState<string | null>(
    null
  );
  const [processingAction, setProcessingAction] = useState<
    "generate" | "resend" | null
  >(null);

  // Debounce search for API calls (50ms)
  const searchQuery = useDebouncedValue(searchInput, 50);
  
  // URQL hooks
  const {
    orders: allOrders,
    totalCount,
    isLoading,
    filter,
    setFilter,
    refresh,
  } = useAdminOrders({ limit: 100 });
  
  const { generateTickets } = useGenerateTickets();
  const { resendTickets } = useResendTickets();
  
  // Update filter when search changes
  useEffect(() => {
    setFilter({ ...filter, search: searchQuery.trim() || undefined });
  }, [searchQuery]);
  
  // Polling for live updates
  useEffect(() => {
    const interval = setInterval(() => {
      refresh();
    }, POLLING_INTERVAL_MS);
    
    return () => clearInterval(interval);
  }, [refresh]);

  // Hide content until scroll restoration is complete (prevents visual jump)
  const [isScrollRestored, setIsScrollRestored] = useState(true);

  // Track scroll restoration state
  const scrollRestorationRef = useRef<{
    orderId: string | null;
    needsRestore: boolean;
  }>({ orderId: null, needsRestore: false });

  // Read from sessionStorage ONCE on mount (useLayoutEffect runs before paint)
  useLayoutEffect(() => {
    const savedOrderId = sessionStorage.getItem(EXPANDED_ORDER_STORAGE_KEY);

    // Clear storage immediately
    sessionStorage.removeItem(SCROLL_STORAGE_KEY);
    sessionStorage.removeItem(EXPANDED_ORDER_STORAGE_KEY);

    if (savedOrderId) {
      scrollRestorationRef.current = {
        orderId: savedOrderId,
        needsRestore: true,
      };
      // Hide content until scroll is restored
      setIsScrollRestored(false);
      // Set expanded order immediately
      setExpandedOrderId(savedOrderId);
    }
  }, []);

  // Save filter to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(LIFECYCLE_FILTER_STORAGE_KEY, lifecycleStatus);
  }, [lifecycleStatus]);

  // Toggle card expansion (only one at a time)
  const handleToggleOrder = useCallback((orderId: string) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  }, []);

  const handleGenerate = useCallback(
    async (orderId: string) => {
      setProcessingOrderId(orderId);
      setProcessingAction("generate");
      try {
        const result = await generateTickets(orderId);
        if (result) {
          const msg = result.message ?? "Билеты сгенерированы";
          safeShowAlert(msg);
          refresh();
        }
      } finally {
        setProcessingOrderId(null);
        setProcessingAction(null);
      }
    },
    [generateTickets, refresh]
  );

  const handleResend = useCallback(
    async (orderId: string) => {
      setProcessingOrderId(orderId);
      setProcessingAction("resend");
      try {
        const result = await resendTickets(orderId);
        if (result) {
          const msg = result.message ?? "Билеты отправлены";
          safeShowAlert(msg);
          refresh();
        }
      } finally {
        setProcessingOrderId(null);
        setProcessingAction(null);
      }
    },
    [resendTickets, refresh]
  );

  // Show loading when initial load
  const isSearching = isLoading;

  // Filter orders client-side based on lifecycle status
  const orders = useMemo(() => {
    if (lifecycleStatus === "all") {
      return allOrders;
    }
    if (lifecycleStatus === "rejected") {
      return allOrders.filter(
        (order) =>
          order.status === "REJECTED" ||
          order.status === "CANCELLED" ||
          order.status === "EXPIRED"
      );
    }
    // Map lifecycle status to backend status for filtering
    const backendStatus = getBackendStatusFilter(lifecycleStatus);
    if (backendStatus) {
      if (lifecycleStatus === "verified") {
        return allOrders.filter((o) => matchesVerifiedFilter(o.status));
      }
      return allOrders.filter((order) => order.status === backendStatus);
    }
    return allOrders;
  }, [allOrders, lifecycleStatus]);

  // Calculate counts for each status from all orders
  const statusCounts = useMemo(() => {
    const counts: Partial<Record<OrderLifecycleStatus, number>> = {
      all: totalCount,
      awaiting_payment: 0,
      pending_verification: 0,
      verified: 0,
      ticket_issued: 0,
      rejected: 0,
    };

    for (const order of allOrders) {
      switch (order.status) {
        case "AWAITING_PAYMENT":
          counts.awaiting_payment = (counts.awaiting_payment ?? 0) + 1;
          break;
        case "PENDING_VERIFICATION":
          counts.pending_verification = (counts.pending_verification ?? 0) + 1;
          break;
        case "VERIFIED":
          counts.verified = (counts.verified ?? 0) + 1;
          break;
        case "TICKET_ISSUED":
          counts.ticket_issued = (counts.ticket_issued ?? 0) + 1;
          break;
        case "TICKETS_PENDING_SEND":
          counts.verified = (counts.verified ?? 0) + 1;
          break;
        case "REJECTED":
        case "CANCELLED":
        case "EXPIRED":
          counts.rejected = (counts.rejected ?? 0) + 1;
          break;
      }
    }

    return counts;
  }, [allOrders, totalCount]);

  // Restore scroll: scroll to expanded card after data loads
  useLayoutEffect(() => {
    const restoration = scrollRestorationRef.current;

    // Skip if no restoration needed
    if (!restoration.needsRestore) return;
    if (!restoration.orderId) return;

    // Skip if still loading
    if (isLoading) return;

    // Skip if no orders yet
    if (orders.length === 0) return;

    // Mark as done to prevent re-runs
    restoration.needsRestore = false;

    const targetOrderId = restoration.orderId;

    // Poll for the card to appear in DOM and scroll to it
    let attempts = 0;
    const maxAttempts = 30;

    const checkAndScroll = () => {
      const card = document.querySelector(`[data-order-id="${targetOrderId}"]`);
      if (card) {
        // Card found - scroll it into view (center of screen)
        card.scrollIntoView({ block: "center", behavior: "instant" });
        // Show content after scroll
        setIsScrollRestored(true);
      } else if (attempts < maxAttempts) {
        attempts++;
        requestAnimationFrame(checkAndScroll);
      } else {
        // Give up, show content anyway
        setIsScrollRestored(true);
      }
    };

    // Start checking immediately
    checkAndScroll();
  }, [isLoading, orders.length]);

  const hasActiveFilters = !!(lifecycleStatus !== "all" || searchInput.trim());

  return (
    <>
      <div
        style={{
          opacity: isScrollRestored ? 1 : 0,
          transition: isScrollRestored ? "opacity 0.15s ease-out" : "none",
        }}
      >
        {/* Status Slider */}
        <OrderStatusSlider
          value={lifecycleStatus}
          onChange={setLifecycleStatus}
          counts={statusCounts}
        />

        {/* Search */}
        <OrderFilters
          searchQuery={searchInput}
          onSearchChange={setSearchInput}
          isLoading={isSearching}
          onRefresh={refresh}
          showSearch={true}
        />

        {/* Results info */}
        {!isLoading && (
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-[11px] text-slate-400">
              {orders.length === 0
                ? "Нет заказов"
                : totalCount > orders.length
                ? `Показано ${orders.length} из ${totalCount}`
                : `Всего: ${orders.length}`}
            </span>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={() => {
                  setLifecycleStatus("all");
                  setSearchInput("");
                }}
                className="text-[12px] font-medium text-[var(--color-accent)] active:opacity-80"
              >
                Сбросить
              </button>
            )}
          </div>
        )}

        {/* Orders list */}
        {orders.length === 0 && !isLoading ? (
          <div className="py-12 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-slate-100 flex items-center justify-center">
              <IconTicket className="w-6 h-6 text-slate-400" />
            </div>
            <p className="text-[14px] text-slate-500">Заказы не найдены</p>
            {hasActiveFilters && (
              <p className="text-[12px] text-slate-400 mt-1">
                Попробуйте изменить фильтры
              </p>
            )}
          </div>
        ) : (
          <div className="-mx-3 xs:-mx-4 min-[430px]:-mx-5 min-[520px]:-mx-6 sm:-mx-7">
            {orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                showEvent={true}
                isExpanded={expandedOrderId === order.id}
                onToggle={handleToggleOrder}
                onGenerate={handleGenerate}
                onResend={handleResend}
                isProcessing={processingOrderId === order.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Processing overlay - генерация / отправка */}
      {processingOrderId && processingAction && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center">
          <div className="text-center">
            <Loader className="mx-auto mb-4" />
            <p className="text-white text-[16px] font-medium">
              {processingAction === "generate"
                ? "Генерация билетов..."
                : "Отправка..."}
            </p>
            <p className="text-white/60 text-[13px] mt-1">
              {processingAction === "generate"
                ? "Создание изображений билетов"
                : "Отправка билетов пользователю"}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
